import React, { useEffect, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface SavedSchemaV2 {
  version: 2;
  title: string;
  tables: Array<{
    id?: string;
    title?: string;
    rows: number;
    fields: Array<{
      name: string;
      type: string;
      required: boolean;
      unit?: string;
      options?: string[];
    }>;
    values?: Record<string, any>;
  }>;
  metaBox: {
    show: boolean;
    fields: string[];
    values?: Record<string, string>;
  };
  detailsRow: {
    show: boolean;
    rightCols: number;
    values?: Record<string, any>;
  };
  footer: {
    columns: number;
    fields: Array<{ label: string; type: string }>;
    values?: Record<string, any>;
  };
}

interface Props {
  show: boolean;
  onHide: () => void;
  serviceDetails: SavedSchemaV2;
}

// 👉 replace with your navbar logo path
const NAVBAR_WATERMARK_URL = '/src/assets/KIU Logo (1).png';

const ServiceDetailsPreviewModal: React.FC<Props> = ({ show, onHide, serviceDetails }) => {
  const modalId = 'service-details-preview-modal';
  const a4Ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const styleId = `adv-table-style-${modalId}`;
    if (!document.getElementById(styleId)) {
      const s = document.createElement('style');
      s.id = styleId;
      s.innerHTML = `
        .tb-preview-wrap { 
          width: 100%; 
          display: flex;
          justify-content: center;
          margin: 0 auto;
          padding: 0;
        }
        .tb-a4 { 
          position: relative;
          width: 210mm; 
          height: 297mm; 
          background: #fff; 
          padding: 12mm; 
          margin: 0; 
          color: #222; 
          box-sizing: border-box;
          box-shadow: 0 0 0.5cm rgba(0,0,0,.2);
          overflow: hidden; /* keep content inside page */
        }
        /* centered, soft watermark (navbar logo) */
        .tb-a4.watermarked::after {
          content: '';
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          width: 70%;
          height: 70%;
          background-image: url('${NAVBAR_WATERMARK_URL}');
          background-repeat: no-repeat;
          background-position: center center;
          background-size: contain;
          opacity: 0.06;
          pointer-events: none;
          z-index: 0;
        }
        .tb-a4 > * { position: relative; z-index: 1; } /* keep content above watermark */

        /* helper class to remove shadow during capture for a cleaner PDF */
        .tb-a4.no-shadow { box-shadow: none !important; }

        .tb-head { 
          display: flex; 
          justify-content: space-between; 
          align-items: flex-start; 
          gap: 12px; 
          margin-bottom: 10px; 
        }
        .tb-title { 
          font-weight: 700; 
          font-size: 18px; 
        }
        .tb-meta { 
          border: 1px solid #bbb; 
          border-collapse: collapse; 
        }
        .tb-meta th, .tb-meta td { 
          border: 1px solid #bbb; 
          padding: 4px 8px; 
          font-size: 12px; 
        }
        .tb-meta th { 
          background: #f6f6f6; 
          text-align: left; 
        }
        .tb-table { 
          width: 100%; 
          border-collapse: collapse; 
          table-layout: fixed; 
          margin-bottom: 12mm; 
        }
        .tb-table th, .tb-table td { 
          border: 1px solid #999; 
          padding: 6px 8px; 
          font-size: 12px; 
          vertical-align: top; 
          word-wrap: break-word;
        }
        .tb-table th { 
          background: #f3f3f3; 
          text-align: left; 
        }
        .tb-bigline { 
          height: 40px; 
          border: 1px dashed #bbb; 
          border-radius: 6px; 
        }
        .tb-line { 
          height: 18px; 
          border-bottom: 1px solid #bbb; 
        }
        .tb-box { 
          width: 16px; 
          height: 16px; 
          border: 2px solid #666; 
          border-radius: 3px; 
          display: inline-block; 
          vertical-align: middle; 
        }
        .tb-radio { 
          width: 14px; 
          height: 14px; 
          border: 2px solid #666; 
          border-radius: 50%; 
          display: inline-block; 
        }
        .tb-footgrid { 
          display: grid; 
          gap: 8mm; 
          margin-top: 8mm; 
        }
        .tb-foot-item .lbl { 
          font-weight: 600; 
          margin-bottom: 4px; 
          font-size: 12px; 
        }
        .tb-foot-item .sign { 
          height: 24mm; 
          border: 1px dashed #cbd5e1; 
          border-radius: 6px; 
        }
        .tb-foot-item .line { 
          height: 18px; 
          border-bottom: 1px solid #bbb; 
        }
      `;
      document.head.appendChild(s);
    }
    return () => {
      document.getElementById(styleId)?.remove();
    };
  }, [modalId]);

  const pad2 = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  const formatDownloadTimestamp = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = pad2(d.getMonth() + 1);
    const dd = pad2(d.getDate());
    const hh = pad2(d.getHours());
    const mi = pad2(d.getMinutes());
    return `${yyyy}-${mm}-${dd}_${hh}-${mi}`;
  };

  const downloadPdf = async () => {
    if (!a4Ref.current) {
      console.error('A4 content element not found');
      alert('Failed to generate PDF. Please try again.');
      return;
    }

    // Ensure watermark class exists
    a4Ref.current.classList.add('watermarked');

    // Temporarily remove box shadow for a clean capture
    a4Ref.current.classList.add('no-shadow');

    try {
      const canvas = await html2canvas(a4Ref.current, {
        useCORS: true,       // allow logo if served with CORS headers
        allowTaint: true,
        backgroundColor: '#fff',
        scale: 2,            // better resolution
      } as any);

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pageWidth = 210;
      const pageHeight = 297;

      // ❗ Single page export — avoids extra blank pages
      // We captured an element that is exactly A4 size in CSS,
      // so we stretch it to full page. No pagination loop necessary.
      pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, pageHeight, undefined, 'FAST');

      // ⏱️ Add download timestamp to the bottom-right corner
      const stamp = `Downloaded: ${new Date().toLocaleString()}`;
      pdf.setFontSize(8);
      // align right by computing text width or use alignment options in newer jsPDF
      // Here we print at 205mm x 292mm (5mm margin from bottom-right)
      pdf.text(stamp, pageWidth - 5, pageHeight - 5, { align: 'right' as any });

      const ts = formatDownloadTimestamp();
      const safeTitle = (serviceDetails.title || 'Maintenance_Checklist').replace(/[^\w\-]+/g, '_');
      pdf.save(`${safeTitle}_${ts}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      // restore shadow after capture
      a4Ref.current?.classList.remove('no-shadow');
    }
  };

  const cellKey = (tableId: string, r: number, cName: string) => `${tableId}:${r}:${cName}`;

  const getTableId = (table: SavedSchemaV2['tables'][0], tableIdx: number) => {
    if (table.id) return table.id;
    const valueKeys = Object.keys(table.values || {});
    const idFromValues = valueKeys[0]?.split(':')[0];
    return idFromValues || `table_${tableIdx}`;
  };

  const renderCell = (
    tableId: string,
    f: { name: string; type: string; required: boolean; unit?: string; options?: string[] },
    r: number,
    tableIdx: number
  ) => {
    const effectiveTableId = getTableId(serviceDetails.tables[tableIdx], tableIdx);
    const k = cellKey(effectiveTableId, r, f.name);
    const table = serviceDetails.tables[tableIdx];
    const v = table?.values?.[k];

    if (f.type === 'textarea') {
      return <div>{v ?? <div className="tb-bigline" />}</div>;
    }
    if (f.type === 'checkbox') {
      return <span className="tb-box" style={v ? { background: '#666' } : {}} />;
    }
    if (f.type === 'select') {
      return (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {(f.options?.slice(0, 4) || ['Option 1', 'Option 2']).map((o) => (
            <span key={o} style={{ whiteSpace: 'nowrap' }}>
              <span className="tb-radio" style={v === o ? { background: '#666' } : {}} /> {o}
            </span>
          ))}
        </div>
      );
    }
    return (
      <div>
        {v ?? <div className="tb-line" />}
        {f.type === 'number' && v && f.unit && <span style={{ marginLeft: 6, color: '#666' }}>({f.unit})</span>}
      </div>
    );
  };

  const renderDetailsCell = (
    tableId: string,
    c: { name: string; type: string; required: boolean; unit?: string; options?: string[] },
    tableIdx: number
  ) => {
    const effectiveTableId = getTableId(serviceDetails.tables[tableIdx], tableIdx);
    const k = `details:${effectiveTableId}:${c.name}`;
    const v = serviceDetails.detailsRow.values?.[k];
    return <div>{v ?? <div className="tb-bigline" />}</div>;
  };

  const renderFooterField = (f: { label: string; type: string }, idx: number) => {
    const valueKeys = Object.keys(serviceDetails.footer.values || {});
    const k = valueKeys[idx] || f.label;
    const v = serviceDetails.footer.values?.[k];

    return (
      <div className="tb-foot-item" key={f.label}>
        <div className="lbl">{f.label}</div>
        {f.type === 'sign' && <div className="sign">{v}</div>}
        {f.type === 'line' && <div className="line">{v}</div>}
        {(f.type === 'text' || f.type === 'date') && <div>{v ?? <div className="line" />}</div>}
      </div>
    );
  };

  return (
    <Modal show={show} onHide={onHide} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title>{serviceDetails.title || 'Service Details Preview'}</Modal.Title>
        <Button variant="success" size="sm" onClick={downloadPdf} className="no-print ms-2">
          <i className="ti ti-download me-1" /> Download
        </Button>
      </Modal.Header>
      <Modal.Body>
        <div className="tb-preview-wrap">
          {/* add .watermarked to enable the logo overlay in preview & export */}
          <div className="tb-a4 watermarked" ref={a4Ref}>
            <div className="tb-head">
              <div>
                <div className="tb-title">{serviceDetails.title || 'Maintenance Checklist'}</div>
                <div style={{ color: '#666', fontSize: 12, marginTop: 2 }}>
                  Generated: {new Date().toLocaleDateString()}
                </div>
              </div>
              {serviceDetails.metaBox.show && (
                <table className="tb-meta">
                  <thead>
                    <tr>
                      <th colSpan={2}>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serviceDetails.metaBox.fields.map((m) => (
                      <tr key={m}>
                        <td style={{ minWidth: 90 }}>{m}</td>
                        <td style={{ minWidth: 160 }}>
                          {serviceDetails.metaBox.values?.[m] || <div className="tb-line" />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {serviceDetails.tables.map((table, tableIdx) => (
              <div key={getTableId(table, tableIdx)}>
                <div className="fw-semibold mb-2">{table.title?.trim() || `Table #${tableIdx + 1}`}</div>
                <table className="tb-table">
                  <thead>
                    <tr>
                      {table.fields.map((c) => (
                        <th key={c.name}>
                          {c.name || '—'}
                          {c.required && <span style={{ color: '#d32f2f', marginLeft: 6 }}>*</span>}
                          {c.type === 'number' && c.unit && (
                            <span style={{ marginLeft: 6, fontWeight: 400, color: '#666' }}>({c.unit})</span>
                          )}
                        </th>
                      ))}
                      {!table.fields.length && <th>No columns defined</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {serviceDetails.detailsRow.show && table.fields.length > 0 && (
                      <tr>
                        {table.fields.map((c) => (
                          <td key={c.name}>{renderDetailsCell(table.id || `table_${tableIdx}`, c, tableIdx)}</td>
                        ))}
                      </tr>
                    )}
                    {Array.from({ length: table.rows }).map((_, r) => (
                      <tr key={`r-${r}`}>
                        {table.fields.map((c) => (
                          <td key={c.name}>
                            {renderCell(table.id || `table_${tableIdx}`, c, r, tableIdx)}
                          </td>
                        ))}
                        {!table.fields.length && <td />}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}

            <div
              className="tb-footgrid"
              style={{ gridTemplateColumns: `repeat(${serviceDetails.footer.columns}, 1fr)` }}
            >
              {serviceDetails.footer.fields.map((f, idx) => renderFooterField(f, idx))}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="no-print">
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ServiceDetailsPreviewModal;
